import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Archive, Calendar, DollarSign, TrendingUp, TrendingDown, BarChart3, RefreshCw } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'

const ArchivesPage = () => {
  const { user } = useAuth()
  const [archives, setArchives] = useState([])
  const [selectedArchive, setSelectedArchive] = useState(null)
  const [archiveDetails, setArchiveDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('history')
  const [comparison, setComparison] = useState(null)
  const [compareForm, setCompareForm] = useState({
    month1: '',
    month2: ''
  })

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
  // const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api'

  // Fetch archives on component mount
  useEffect(() => {
    if (user) {
      loadArchives()
    }
  }, [user])

  const loadArchives = async () => {
    try {
      setLoading(true)
      
      if (!user) {
        return
      }

      // Get token from localStorage (same way as other components)
      const token = localStorage.getItem('access_token')
      
      if (!token) {
        console.error('No access token found')
        return
      }
      
      const response = await fetch(`${API_BASE_URL}/monthly/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setArchives(data.history || [])
      } else {
        console.error('Failed to load archives:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('Error loading archives:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadArchiveDetails = async (year, month) => {
    try {
      setDetailsLoading(true)
      
      // Get token from localStorage (same way as other components)
      const token = localStorage.getItem('access_token')
      
      if (!token) {
        console.error('❌ No access token found')
        return
      }
      
      const response = await fetch(`${API_BASE_URL}/monthly/archives/${year}/${month}/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setArchiveDetails(data)
        setSelectedArchive({ year, month })
        setActiveTab('details')
      } else {
        console.error('Failed to load archive details:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('Error loading archive details:', error)
    } finally {
      setDetailsLoading(false)
    }
  }

  const compareMonths = async () => {
    if (!compareForm.month1 || !compareForm.month2) return

    try {
      const [year1, month1] = compareForm.month1.split('-')
      const [year2, month2] = compareForm.month2.split('-')
      
      // Get token from localStorage (same way as other components)
      const token = localStorage.getItem('access_token')
      
      if (!token) {
        console.error('❌ No access token found')
        return
      }
      
      const response = await fetch(`${API_BASE_URL}/monthly/compare/${year1}/${month1}/${year2}/${month2}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setComparison(data)
      } else {
        console.error('Failed to compare months:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('Error comparing months:', error)
    }
  }

  const ArchiveCard = ({ archive }) => (
    <div 
      className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105"
      onClick={() => loadArchiveDetails(archive.year, archive.month)}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <Calendar className="h-5 w-5 mr-2 text-primary-600" />
          {archive.period}
        </h3>
        <Archive className="h-5 w-5 text-gray-400" />
      </div>
      
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600 dark:text-gray-400">Total Expenses</span>
          <span className="font-semibold text-gray-900 dark:text-white">
            ${parseFloat(archive.total_amount || 0).toFixed(2)}
          </span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600 dark:text-gray-400">Expense Count</span>
          <span className="font-semibold text-gray-900 dark:text-white">
            {archive.total_expenses || 0}
          </span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600 dark:text-gray-400">Budget Count</span>
          <span className="font-semibold text-gray-900 dark:text-white">
            {archive.total_budgets || 0}
          </span>
        </div>
        
        <div className="flex justify-between items-center pt-2 border-t border-gray-100 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-500">Archived</span>
          <span className="text-xs text-gray-500 dark:text-gray-500">
            {new Date(archive.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>
    </div>
  )

  const CategorySection = ({ title, categories, type }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        {type === 'expenses' ? (
          <DollarSign className="h-5 w-5 mr-2 text-red-500" />
        ) : (
          <BarChart3 className="h-5 w-5 mr-2 text-green-500" />
        )}
        {title}
      </h4>
      
      {Object.keys(categories).length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">No {type} found.</p>
      ) : (
        <div className="space-y-4">
          {Object.entries(categories).map(([category, info]) => (
            <div key={category} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <h5 className="font-medium text-gray-900 dark:text-white">{category}</h5>
                <span className="text-sm font-semibold text-primary-600">
                  ${info.total?.toFixed(2)} ({info.count} items)
                </span>
              </div>
              
              <div className="space-y-2">
                {(info[type] || []).slice(0, 3).map((item, index) => (
                  <div key={index} className="text-sm text-gray-600 dark:text-gray-400 pl-4 border-l-2 border-gray-200 dark:border-gray-600">
                    <div className="font-medium">{item.title || item.category}</div>
                    <div className="flex justify-between">
                      <span>${parseFloat(item.amount).toFixed(2)}</span>
                      <span>{new Date(item.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
                {(info[type] || []).length > 3 && (
                  <div className="text-xs text-gray-500 pl-4">
                    ... and {(info[type] || []).length - 3} more
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      {/* Debug Panel - Remove this in production */}
      {/* <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 text-sm">
        <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">🐛 Debug Info</h4>
        <div className="space-y-1 text-yellow-700 dark:text-yellow-300">
          <div>API URL: {API_BASE_URL}</div>
          <div>User: {user ? '✅ Authenticated' : '❌ Not authenticated'}</div>
          <div>Loading: {loading ? '⏳ Loading...' : '✅ Complete'}</div>
          <div>Archives Count: {archives.length}</div>
          <div>Environment: {import.meta.env.MODE}</div>
        </div>
      </div> */}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Archive className="h-8 w-8 mr-3 text-primary-600" />
            Monthly Archives
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            View your previous months' expenses and budgets
          </p>
        </div>
        
        <button
          onClick={loadArchives}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('history')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'history'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            📅 Monthly History
          </button>
          <button
            onClick={() => setActiveTab('details')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'details'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            📊 Details
          </button>
          <button
            onClick={() => setActiveTab('compare')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'compare'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            🔄 Compare
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'history' && (
        <div>
          {archives.length === 0 ? (
            <div className="text-center py-12">
              <Archive className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No archives found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Complete a monthly reset to create your first archive.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {archives.map((archive) => (
                <ArchiveCard key={`${archive.year}-${archive.month}`} archive={archive} />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'details' && (
        <div>
          {detailsLoading ? (
            <LoadingSpinner />
          ) : archiveDetails ? (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  📊 {archiveDetails.month_name} {archiveDetails.year} Summary
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">${archiveDetails.statistics?.total_expenses?.toFixed(2)}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Expenses</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">${archiveDetails.statistics?.total_budgets?.toFixed(2)}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Budgets</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${archiveDetails.statistics?.net_budget >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${archiveDetails.statistics?.net_budget?.toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Net Budget</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{archiveDetails.statistics?.expense_count}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Expense Count</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{archiveDetails.statistics?.budget_count}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Budget Count</div>
                  </div>
                </div>
              </div>

              {/* Categories */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CategorySection 
                  title="💰 Expenses by Category" 
                  categories={archiveDetails.expenses_by_category || {}}
                  type="expenses"
                />
                <CategorySection 
                  title="🎯 Budgets by Category" 
                  categories={archiveDetails.budgets_by_category || {}}
                  type="budgets"
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Select a month to view details
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Click on any archive card from the history tab to view detailed information.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'compare' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              🔄 Compare Months
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  First Month
                </label>
                <select
                  value={compareForm.month1}
                  onChange={(e) => setCompareForm({...compareForm, month1: e.target.value})}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select a month...</option>
                  {archives.map((archive) => (
                    <option key={`${archive.year}-${archive.month}`} value={`${archive.year}-${archive.month}`}>
                      {archive.period}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Second Month
                </label>
                <select
                  value={compareForm.month2}
                  onChange={(e) => setCompareForm({...compareForm, month2: e.target.value})}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select a month...</option>
                  {archives.map((archive) => (
                    <option key={`${archive.year}-${archive.month}`} value={`${archive.year}-${archive.month}`}>
                      {archive.period}
                    </option>
                  ))}
                </select>
              </div>
              
              <button
                onClick={compareMonths}
                disabled={!compareForm.month1 || !compareForm.month2}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Compare
              </button>
            </div>
          </div>

          {comparison && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                📊 Comparison Results
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Month 1 */}
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <h5 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {comparison.month1?.month_name} {comparison.month1?.year}
                  </h5>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Expenses: </span>
                      <span className="font-medium">${comparison.month1?.total_expenses?.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Budgets: </span>
                      <span className="font-medium">${comparison.month1?.total_budgets?.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Net: </span>
                      <span className="font-medium">${comparison.month1?.net_budget?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Changes */}
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h5 className="font-semibold text-gray-900 dark:text-white mb-2">📈 Changes</h5>
                  <div className="space-y-2">
                    <div className="flex items-center justify-center">
                      {comparison.comparison?.expense_difference >= 0 ? (
                        <TrendingUp className="h-4 w-4 text-red-500 mr-1" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-green-500 mr-1" />
                      )}
                      <span className={`font-medium ${comparison.comparison?.expense_difference >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {comparison.comparison?.expense_difference >= 0 ? '+' : ''}${comparison.comparison?.expense_difference?.toFixed(2)} 
                        ({comparison.comparison?.expense_change_percentage?.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Expense Change</div>
                  </div>
                </div>

                {/* Month 2 */}
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <h5 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {comparison.month2?.month_name} {comparison.month2?.year}
                  </h5>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Expenses: </span>
                      <span className="font-medium">${comparison.month2?.total_expenses?.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Budgets: </span>
                      <span className="font-medium">${comparison.month2?.total_budgets?.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Net: </span>
                      <span className="font-medium">${comparison.month2?.net_budget?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ArchivesPage
